import { AppleLoginButton } from 'app/components/authentication/AppleLoginButton';
import { AuthenticationMethodOption } from 'app/components/authentication/AuthenticationMethodOption';
import { FacebookLoginButton } from 'app/components/authentication/FacebookLoginButton';
import { GoogleLoginButton } from 'app/components/authentication/GoogleLoginButton';
import { withErrorBoundary } from 'app/components/error-boundary';
import { LoginAsPromptModal } from 'app/components/use-login-as-prompt';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { LogoCanada, LogoTwoLines } from 'app/design/branding/logo';
import { images } from 'app/design/images';
import { PCImage } from 'app/design/primitives/image';
import { useForm } from 'app/hooks/form';
import { useRoute } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { useLastAuthenticationMethod } from 'app/lib/authentication-method';
import { env } from 'app/lib/env';
import { prepareLoginAsEmail } from 'app/lib/login-as-email';
import { useCreateIdentity, type CreateTokenDTO } from 'app/network/identity';
import { useSocialConnect } from 'app/screens/onboarding/signup';
import { createTheme as createDSTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { usePageTitle } from 'app/utils/web-helpers';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, type StyleProp, type TextInput, type View, type ViewStyle } from 'react-native';
import * as yup from 'yup';
import { IntroLoginPage } from './intro-login-page.large';
import { IntroSignupPage } from './intro-signup-page.large';
const theme = createDSTheme();

const IntroScreenComponent: NavigationScreen<'onboardingIntro'> = ({ navigation }) => {
  const { t: tLogin } = useTranslation('login');
  const { t: tSplash } = useTranslation('splash');
  const route = useRoute<'onboardingIntro'>();

  const isLoginPage = route.params?.login;
  const lastUsedMethod = useLastAuthenticationMethod(Boolean(isLoginPage));

  const setPageTitle = usePageTitle('onboardingIntro');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const onForgotPassword = () => {
    navigation.navigate('password-recovery', undefined, { pop: true });
  };

  const { handleSocialIdP, loginButtonsState, didSSOLogin, ssoError, goBackWhenIsLoggedIn, setGoBackWhenIsLoggedIn } =
    useSocialConnect(() => {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.reset({ routes: [{ name: 'main' }] });
      }
    });

  const schema: yup.ObjectSchema<CreateTokenDTO> = yup.object({
    email: yup
      .string()
      .required()
      .test({
        name: 'customEmailValidation',
        message: tLogin('fieldErrorDefault', { ns: 'common' }),
        test: (value) =>
          // 'email1 -> email2' value is for login-as; see handleSubmit() below
          value.includes(' -> ') || yup.string().email().isValidSync(value),
      }),
    password: yup.string().min(8).required(),
  });
  const { control, formState, handleSubmit } = useForm({
    schema,
    validationMode: 'onBlur',
    defaultValues:
      env.APP_ENV === 'local'
        ? { email: env.DEFAULT_USERNAME ?? '', password: env.DEFAULT_PASSWORD ?? '' }
        : { email: '', password: '' },
  });

  const createIdentity = useCreateIdentity({
    onSuccess: () => {
      track(Event.LoginSuccess, { provider: 'Email' });
      // Wait until isLoggedIn atom is set, then go back to the Babillard
      setGoBackWhenIsLoggedIn(true);
    },
  });

  const onSubmit = handleSubmit(async (form: CreateTokenDTO) => {
    form.email = await prepareLoginAsEmail(form.email);
    createIdentity.mutate(form);
  });

  const onPressSignUp = () => {
    navigation.navigate(
      'onboarding',
      {
        screen: 'contact-information',
      },
      { pop: true },
    );
  };

  const onToggleForm = () => {
    navigation.setParams({ login: !isLoginPage });
  };

  const refPassword = React.useRef<TextInput>(null);

  const ssoSize = React.useRef(0);
  const refSSO = React.useRef<View>(null);

  const onLayoutSSO = () => {
    if (!refSSO.current) return;
    if (ssoSize.current) return;
    refSSO.current.measure((_x, _y, _width, height, _pageX, _pageY) => {
      if (height) {
        ssoSize.current = height + 2 * theme.spacing['spacing.general.x4'];
      }
    });
  };

  const renderSSOButtons = ({ style }: { style?: StyleProp<ViewStyle> }) => (
    <Row gap="spacing.general.x2" justifyContent="space-evenly" style={style}>
      <AuthenticationMethodOption flex lastUsedMethod={lastUsedMethod} method="Facebook">
        <FacebookLoginButton handleSocialIdP={handleSocialIdP} iconOnly loginButtonsState={loginButtonsState} />
      </AuthenticationMethodOption>

      {Platform.OS !== 'android' ? (
        <AuthenticationMethodOption flex lastUsedMethod={lastUsedMethod} method="Apple">
          <AppleLoginButton handleSocialIdP={handleSocialIdP} iconOnly loginButtonsState={loginButtonsState} />
        </AuthenticationMethodOption>
      ) : null}

      <AuthenticationMethodOption flex lastUsedMethod={lastUsedMethod} method="Google">
        <GoogleLoginButton handleSocialIdP={handleSocialIdP} iconOnly loginButtonsState={loginButtonsState} />
      </AuthenticationMethodOption>
    </Row>
  );

  const renderPartners = () =>
    isWhiteLabel() ? (
      <Row
        style={{
          marginTop: theme.spacing['spacing.general.x8'],
          bottom: theme.spacing['spacing.general.x10'],
        }}
      >
        {isWhiteLabel(WhiteLabel.Refrain) ? (
          <Column flex={1} alignItems="center">
            <Text textAlign="center">{tSplash('title')}</Text>
            <LogoCanada color={theme.colors['text.primary']} width={100} height={50} />
          </Column>
        ) : null}
        {isWhiteLabel(WhiteLabel.Mutuo) ? (
          <Column flex={1} alignItems="center" gap="spacing.general.x3">
            <Text textAlign="center">{tSplash('title')}</Text>
            <PCImage source={images.partnersDark} width={180} aspectRatio={373 / 128} />
          </Column>
        ) : null}
        <Column flex={1} alignItems="center" gap="spacing.general.x3">
          <Text textAlign="center">{tSplash('poweredBy')}</Text>
          <Column style={{ bottom: theme.spacing['spacing.general.x5'] }}>
            <LogoTwoLines width="100%" height={120} color={theme.colors['text.primary']} />
          </Column>
        </Column>
      </Row>
    ) : null;

  const contentPage = isLoginPage ? (
    <IntroLoginPage
      lastUsedMethod={lastUsedMethod}
      control={control}
      createIdentity={createIdentity}
      didSSOLogin={didSSOLogin}
      goBackWhenIsLoggedIn={goBackWhenIsLoggedIn}
      isFormValid={formState.isValid}
      onForgotPassword={onForgotPassword}
      onLayoutSSO={onLayoutSSO}
      onSubmit={onSubmit}
      onToggleForm={onToggleForm}
      refPassword={refPassword}
      refSSO={refSSO}
      renderPartners={renderPartners}
      renderSSOButtons={renderSSOButtons}
      ssoError={ssoError}
    />
  ) : (
    <IntroSignupPage
      createIdentity={createIdentity}
      didSSOLogin={didSSOLogin}
      goBackWhenIsLoggedIn={goBackWhenIsLoggedIn}
      onLayoutSSO={onLayoutSSO}
      onPressSignUp={onPressSignUp}
      onToggleForm={onToggleForm}
      refSSO={refSSO}
      renderPartners={renderPartners}
      renderSSOButtons={renderSSOButtons}
      ssoError={ssoError}
    />
  );

  return (
    <>
      {contentPage}
      <LoginAsPromptModal />
    </>
  );
};

export const IntroScreen: NavigationScreen<'onboardingIntro'> = withErrorBoundary(IntroScreenComponent);
